// RAWeb 9.1 — Normalisation runtime de la hiérarchie des titres.
//
// Filet de sécurité : après le rendu, on parcourt les titres du contenu principal
// (<h1>..<h6> ET [role="heading"][aria-level]) dans l'ordre du DOM et on supprime
// les SAUTS de niveau (ex. un <h1> suivi directement d'un titre de niveau 3 quand
// la section n'a pas de <h2>). On n'écrit QUE role/aria-level : les balises et le
// visuel (classes CSS) ne changent jamais, donc aucun impact SEO (Google lit les
// balises, pas aria-level).
//
// Le choix des niveaux côté CMS est respecté : on n'intervient que là où il y a
// réellement un saut. L'algorithme est à pile pour garder les frères cohérents
// (toutes les cartes d'un même groupe restent au même niveau), au lieu d'un simple
// « niveau = précédent + 1 » qui les désynchroniserait.

(function () {
  function getScope() {
    return document.querySelector("main") || document.body;
  }

  function isRendered(el) {
    if (el.closest('[aria-hidden="true"]')) return false;
    // un titre masqué (display:none, x-cloak, clone Swiper caché…) est ignoré,
    // comme le font axe/equalweb, pour ne pas fausser la numérotation.
    return el.getClientRects().length > 0;
  }

  function tagLevel(el) {
    return /^H[1-6]$/.test(el.tagName) ? parseInt(el.tagName[1], 10) : null;
  }

  function declaredLevel(el) {
    const aria = parseInt(el.getAttribute("aria-level"), 10);
    if (!Number.isNaN(aria)) return aria;
    return tagLevel(el) || 1;
  }

  function normalize() {
    try {
      const scope = getScope();
      if (!scope) return;
      const nodes = scope.querySelectorAll('h1,h2,h3,h4,h5,h6,[role="heading"]');
      const rendered = [...nodes].filter(isRendered);

      // RAWeb 9.1 (page-has-heading-one) : si aucun titre de niveau 1 n'est présent
      // dans le contenu principal, on promeut le tout premier titre en <h1> (via
      // aria-level) pour garantir un point d'entrée. Filet pour les pages dont le
      // titre principal n'est pas balisé comme titre (ex. certains gabarits listing).
      const hasLevelOne = rendered.some((el) => declaredLevel(el) === 1);

      const stack = []; // { declared, output }
      rendered.forEach((el, index) => {
        const declared = declaredLevel(el);
        while (stack.length && stack[stack.length - 1].declared >= declared) stack.pop();
        // premier titre (ou titre plus « haut » que tous ses prédécesseurs) :
        // on conserve son niveau ; sinon au plus « parent + 1 » (jamais de saut).
        let output = stack.length ? stack[stack.length - 1].output + 1 : declared;
        // garantir un h1 : le premier titre devient niveau 1 s'il n'y en a aucun.
        if (index === 0 && !hasLevelOne) output = 1;
        stack.push({ declared, output });

        const current = declaredLevel(el);
        if (output !== current) {
          el.setAttribute("role", "heading");
          el.setAttribute("aria-level", String(output));
        } else if (tagLevel(el) === output && el.getAttribute("aria-level") === String(output)) {
          // aria-level redondant avec la balise native : on nettoie.
          el.removeAttribute("aria-level");
        }
      });
    } catch (e) {
      /* ne jamais casser la page pour un souci de titres */
    }
  }

  function run() {
    normalize();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", run);
  } else {
    run();
  }

  // Contenu injecté après coup (carrousels Swiper, x-show Alpine, lazyload) :
  // ré-exécution debounce (une passe par frame). On n'observe que childList, et
  // comme normalize() n'écrit que des attributs, il ne se re-déclenche pas lui-même.
  let scheduled = false;
  const observer = new MutationObserver(() => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      normalize();
    });
  });

  function startObserving() {
    observer.observe(getScope(), { childList: true, subtree: true });
  }
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", startObserving);
  } else {
    startObserving();
  }
})();
